import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getNavConfig, saveNavConfig, updateNavbarInEditor } from '../plugins/globalNavbar';

// Design tokens matching dashboard.css
const t = {
  bg:         '#0d0d0f',
  surface:    '#17171a',
  surface2:   '#1d1d22',
  elevated:   '#222228',
  border:     '#28282f',
  borderSoft: '#1f1f25',
  text:       '#ededf2',
  text2:      '#a3a3ad',
  text3:      '#6b6b76',
  accent:     '#3b82f6',
  rose:       '#f43f5e',
};

const styles = {
  backdrop: {
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'rgba(0,0,0,0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '16px',
    fontFamily: "'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif",
    fontSize: '14px',
    WebkitFontSmoothing: 'antialiased',
  },
  panel: {
    background: t.surface,
    border: `1px solid ${t.borderSoft}`,
    borderRadius: '16px',
    width: '100%', maxWidth: '520px',
    display: 'flex', flexDirection: 'column',
    maxHeight: '90vh',
    boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '20px 20px 16px',
    borderBottom: `1px solid ${t.borderSoft}`,
  },
  title: {
    fontSize: '15px', fontWeight: 600,
    color: t.text, letterSpacing: '-0.01em', margin: 0,
  },
  closeBtn: {
    width: 28, height: 28,
    borderRadius: 7,
    border: 'none',
    background: 'none',
    color: t.text3,
    cursor: 'pointer',
    display: 'grid', placeItems: 'center',
    transition: 'background 120ms, color 120ms',
    fontSize: 18,
    lineHeight: 1,
  },
  body: {
    padding: '20px',
    overflowY: 'auto',
    flex: 1,
  },
  hint: {
    fontSize: '12.5px', color: t.text3, marginBottom: '16px',
  },
  row: {
    display: 'flex', alignItems: 'center', gap: 8,
    marginBottom: 8,
  },
  inputWrap: {
    display: 'flex', flexDirection: 'column', gap: 2, flex: 1,
  },
  label: {
    fontSize: '10.5px', fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: '0.05em', color: t.text3,
  },
  input: {
    background: t.bg,
    border: `1px solid ${t.borderSoft}`,
    borderRadius: 8,
    padding: '8px 10px',
    color: t.text,
    fontSize: '13px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    transition: 'border-color 140ms',
  },
  deleteBtn: {
    width: 28, height: 28, flexShrink: 0,
    border: 'none', background: 'none',
    borderRadius: 7,
    color: t.text3,
    cursor: 'pointer',
    display: 'grid', placeItems: 'center',
    transition: 'background 120ms, color 120ms',
  },
  addBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 13px',
    background: 'none',
    border: `1px dashed ${t.border}`,
    borderRadius: 9,
    color: t.text2,
    fontSize: '13px', fontWeight: 500,
    cursor: 'pointer',
    marginTop: 8,
    transition: 'border-color 140ms, color 140ms',
    fontFamily: 'inherit',
  },
  footer: {
    display: 'flex', justifyContent: 'flex-end', gap: 8,
    padding: '14px 20px',
    borderTop: `1px solid ${t.borderSoft}`,
  },
  cancelBtn: {
    padding: '8px 16px',
    borderRadius: 9,
    border: `1px solid ${t.borderSoft}`,
    background: t.surface,
    color: t.text2,
    fontSize: '13px', fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'background 140ms',
  },
  saveBtn: {
    padding: '8px 18px',
    borderRadius: 9,
    border: 'none',
    background: t.accent,
    color: 'white',
    fontSize: '13px', fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
    boxShadow: '0 1px 0 rgba(255,255,255,0.18) inset, 0 4px 14px rgba(59,130,246,0.25)',
    transition: 'filter 140ms',
  },
};

export default function NavigationSettingsModal({ open, onClose }) {
  const [links, setLinks] = useState([]);

  useEffect(() => {
    if (open) setLinks(getNavConfig(window.editor));
  }, [open]);

  if (!open) return null;

  const update = (i, field, val) =>
    setLinks(prev => prev.map((l, j) => (j === i ? { ...l, [field]: val } : l)));

  const handleSave = () => {
    const cleaned = links.filter(l => l.label.trim());
    saveNavConfig(cleaned);
    if (window.editor) updateNavbarInEditor(window.editor);
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <div style={styles.backdrop} onClick={handleBackdropClick}>
      <div style={styles.panel}>

        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>Navigation Menu</h2>
          <button
            style={styles.closeBtn}
            onClick={onClose}
            onMouseEnter={e => { e.currentTarget.style.background = t.elevated; e.currentTarget.style.color = t.text; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = t.text3; }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={styles.body}>
          <p style={styles.hint}>Choose which pages appear in your site's navigation bar.</p>

          {links.map((link, i) => (
            <div key={i} style={styles.row}>
              {/* Label field */}
              <div style={{ ...styles.inputWrap, flex: '0 0 140px' }}>
                <span style={styles.label}>Label</span>
                <input
                  style={styles.input}
                  value={link.label}
                  placeholder="Home"
                  onChange={e => update(i, 'label', e.target.value)}
                  onFocus={e => { e.currentTarget.style.borderColor = t.accent; }}
                  onBlur={e => { e.currentTarget.style.borderColor = t.borderSoft; }}
                />
              </div>

              {/* URL field */}
              <div style={styles.inputWrap}>
                <span style={styles.label}>URL</span>
                <input
                  style={styles.input}
                  value={link.href}
                  placeholder="/page"
                  onChange={e => update(i, 'href', e.target.value)}
                  onFocus={e => { e.currentTarget.style.borderColor = t.accent; }}
                  onBlur={e => { e.currentTarget.style.borderColor = t.borderSoft; }}
                />
              </div>

              {/* Delete */}
              <button
                style={{ ...styles.deleteBtn, marginTop: 16 }}
                disabled={links.length <= 1}
                onClick={() => setLinks(prev => prev.filter((_, j) => j !== i))}
                onMouseEnter={e => { if (links.length > 1) { e.currentTarget.style.background = 'rgba(244,63,94,0.12)'; e.currentTarget.style.color = t.rose; } }}
                onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = t.text3; }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M1 3h12M4.5 3V2a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v1M5.5 6v5M8.5 6v5M2 3l.8 8.1A1 1 0 0 0 3.8 12h6.4a1 1 0 0 0 1-.9L12 3" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          ))}

          <button
            style={styles.addBtn}
            onClick={() => setLinks(prev => [...prev, { label: '', href: '/' }])}
            onMouseEnter={e => { e.currentTarget.style.borderColor = t.accent; e.currentTarget.style.color = t.accent; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = t.border; e.currentTarget.style.color = t.text2; }}
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              <path d="M6.5 1v11M1 6.5h11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            Add page
          </button>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button
            style={styles.cancelBtn}
            onClick={onClose}
            onMouseEnter={e => { e.currentTarget.style.background = t.elevated; }}
            onMouseLeave={e => { e.currentTarget.style.background = t.surface; }}
          >
            Cancel
          </button>
          <button
            style={styles.saveBtn}
            onClick={handleSave}
            onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.08)'; }}
            onMouseLeave={e => { e.currentTarget.style.filter = 'none'; }}
          >
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
